import { FC, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { Button, Spinner, Heading, Pressable, IconButton } from '../../../shared/ui';
import { IPsyBlock } from '../new-questions/new-question-psy';
import { IPsySetting } from '../../../store/psyTools.slice';
import { shuffleArray } from '../../../utils/textHelpers';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { clearMacClientSelections, clearMacSharedView } from '../../../store/mac.slice';

import { ShuffleIcon } from '../../icons/shuffle';

import { MacCardPrompt } from './mac-card-prompt';
import { MacCardsSelected } from './mac-card-selected';
import { MacResultsMobile } from './mac-results-mobile';
import { MacDeckGalleryModal } from './mac-deck-gallery-modal';

const makeUrl = (url: string) => {
  if (!url) return '';

  return url.startsWith('http') ? url : `${import.meta.env.VITE_REACT_APP_API_URL}${url}`;
};

export interface IMacCardsProps extends IPsyBlock {
  onCardZoom?: (zoomed: boolean) => void;
  /** Отправка колоды всем клиентам через data channel (LiveKit) */
  sendMacCardsOpen?: (data: {
    show: boolean;
    cover: string;
    color: string;
    result: { name: string; value: string[] };
  }) => void;
  /** Мобильный 1:1: выбор колоды и подсказка в нижней шторке, результаты — на весь экран */
  mobile?: boolean;
}
type DeckSettingEntry = {
  value?: string[];
};

export interface IDeckPreviewProps {
  deck: IPsySetting;
  isSelected: boolean;
  coverDown: boolean;
  onSelect: (id: number) => void;
  onInspect: (deck: IPsySetting) => void;
  /** Мобильный 1:1: крупная кнопка «?», отметка выбранной колоды, заглушка вместо спиннера */
  mobile?: boolean;
}

export const DeckPreview: FC<IDeckPreviewProps> = ({
  deck,
  isSelected,
  coverDown,
  onSelect,
  onInspect,
  mobile = false,
}) => {
  const primarySettings = Array.isArray(deck.settings) ? (deck.settings[0] as DeckSettingEntry | undefined) : undefined;
  const deckSettings = Array.isArray(primarySettings?.value) ? (primarySettings?.value as string[]) : [];
  const coverImage = deckSettings?.[0] || '';
  const borderColor = deckSettings?.[1] || 'transparent';
  const deckValues = Array.isArray(deck.value) ? deck.value : [];
  const faceImage = deckValues?.[0] || '';
  const coverSrc = coverImage ? makeUrl(coverImage) : '';
  const faceSrc = faceImage ? makeUrl(faceImage) : '';

  const [coverLoaded, setCoverLoaded] = useState(!coverSrc);
  const [faceLoaded, setFaceLoaded] = useState(!faceSrc);
  const [isFlipping, setIsFlipping] = useState(false);

  useEffect(() => {
    setCoverLoaded(!coverSrc);
  }, [coverSrc]);

  useEffect(() => {
    setFaceLoaded(!faceSrc);
  }, [faceSrc]);

  useEffect(() => {
    setIsFlipping(true);
    const timer = setTimeout(() => setIsFlipping(false), 700);

    return () => clearTimeout(timer);
  }, [coverDown]);

  const showSpinner = coverDown ? !!faceSrc && !faceLoaded : !!coverSrc && !coverLoaded;
  const transformValue = `${coverDown ? 'rotateY(180deg)' : 'rotateY(0deg)'} ${isFlipping ? 'scale(1.03)' : 'scale(1)'}`;

  const defaultBorderColor = '#6A6B70';

  return (
    <div className="flex w-full min-w-0 flex-col items-stretch gap-[5px] lg:gap-[10px] hover:cursor-pointer">
      <div
        style={{
          borderWidth: '2px',
          borderColor: isSelected ? borderColor : coverDown ? 'var(--color-ink-muted)' : defaultBorderColor,
        }}
        className="w-full aspect-[110/140] rounded-control relative overflow-hidden flex items-center justify-center text-white border-2 border-ink-muted bg-transparent"
        onClick={() => onSelect(deck.id)}
      >
        <IconButton
          label="Просмотреть все карты колоды"
          variant="overlay"
          className={mobile ? 'absolute top-1.5 right-1.5 z-30' : 'absolute top-1 right-1 z-30'}
          onClick={(e) => {
            e.stopPropagation();
            onInspect(deck);
          }}
        >
          ?
        </IconButton>
        {mobile && isSelected && (
          <span className="absolute left-1.5 top-1.5 z-30 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-ink shadow-control">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M5 12.5l4.5 4.5L19 7.5"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
        <div style={{ perspective: '1200px' }} className="relative w-full h-full">
          <div
            className="relative w-full h-full transition-transform duration-700"
            style={{
              transformStyle: 'preserve-3d',
              transform: transformValue,
              transitionTimingFunction: 'cubic-bezier(0.4, 0.2, 0.2, 1)',
            }}
          >
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                backfaceVisibility: 'hidden',
              }}
            >
              {coverSrc ? (
                <img
                  className="object-cover w-full h-full"
                  src={coverSrc}
                  alt={`${deck.name}-cover`}
                  onLoad={() => setCoverLoaded(true)}
                  onError={() => setCoverLoaded(true)}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-[rgba(0,0,0,0.65)] px-2 text-center">
                  <p className="uppercase text-heading-md">{deck.name}</p>
                </div>
              )}
            </div>

            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: 'rotateY(180deg)',
                backfaceVisibility: 'hidden',
              }}
            >
              {faceSrc ? (
                <img
                  className="object-cover w-full h-full"
                  src={faceSrc}
                  alt={`${deck.name}-face`}
                  onLoad={() => setFaceLoaded(true)}
                  onError={() => setFaceLoaded(true)}
                />
              ) : (
                <div className="px-2 text-center text-ui-xs-strong uppercase tracking-wide">
                  Нет изображения лицевой стороны
                </div>
              )}
            </div>
          </div>
        </div>

        {showSpinner &&
          (mobile ? (
            <div
              className="absolute inset-0 animate-pulse bg-white/10 pointer-events-none"
              style={{ zIndex: 15 }}
              aria-hidden
            />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.35)', zIndex: 15 }}
            >
              <div className="flex items-center justify-center">
                <Spinner size="lg" className="text-ink-subtle" />
              </div>
            </div>
          ))}

        {coverDown && (
          <div
            className="absolute bottom-0 left-0 w-full text-white text-micro leading-tight text-center select-none"
            style={{
              background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.45) 45%, rgba(0, 0, 0, 0.85) 100%)',
              minHeight: '32px',
              padding: '6px 6px 4px',
              zIndex: 20,
            }}
            title={deck.name.length > 16 ? deck.name : undefined}
          >
            <span
              className="block w-full overflow-hidden text-ellipsis whitespace-normal line-clamp-2 break-words"
              style={{ maxWidth: '100%' }}
              title={deck.name.length > 16 ? deck.name : undefined}
            >
              {deck.name}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const MacCardsPsy: FC<IMacCardsProps> = ({
  setResult,
  onCardZoom,
  onToolActivated,
  sendMacCardsOpen,
  mobile = false,
}) => {
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);
  const dispatch = useAppDispatch();
  const activeTool = useAppSelector((state) => state.activeToolStateSlice.activeTool);
  const selectedCards = useAppSelector((state) => state.macStateSlice.selected);
  const clientSelectionsByParticipant = useAppSelector((state) => state.macStateSlice.clientSelectionsByParticipant);

  const [selectedCard, setSelectedCard] = useState<null | number>(null);
  const [coverDown, setCoverDown] = useState(false);
  const [inspectedDeck, setInspectedDeck] = useState<IPsySetting | null>(null);
  const [activeClientTab, setActiveClientTab] = useState<string | null>(null);
  const lastSavedCardsSignatureRef = useRef<string | null>(null);
  const clientParticipantIds = Object.keys(clientSelectionsByParticipant);
  const cards = psyTools.find((el) => el.name === 'metaforic_cards')?.psy_tool_settings || [];

  const arr = cards;
  const activateTool = () => {
    const selectedSettings = cards.find((el) => el.id === selectedCard)?.settings?.[0]?.value || [];
    const cover = selectedSettings[0] || '';
    const color = selectedSettings[1] || '';
    const selectedDeck = cards.find((el) => el.id === selectedCard);
    const deck = selectedDeck?.value || [];
    const payload = {
      show: coverDown,
      cover,
      color,
      result: {
        name: selectedDeck?.name || '',
        value: shuffleArray(Object.values(deck)),
      },
    };

    dispatch(clearMacClientSelections());
    dispatch(clearMacSharedView());
    sendMacCardsOpen?.(payload);

    onToolActivated?.('metaforic_cards', {
      deck_id: selectedDeck?.id ?? null,
      deck_name: selectedDeck?.name ?? null,
      cover_mode: coverDown ? 'face_up' : 'face_down',
    });
    dispatch(setActiveTool(PsyToolsId.MAC_CARDS));
    // onSetMenu(0);
    // if (selectedCard) {
    //   setShowCards && setShowCards(coverUp);
    // }
  };

  useEffect(() => {
    if (selectedCards.length === 0) {
      lastSavedCardsSignatureRef.current = null;
      return;
    }

    const selectedCardImages = selectedCards.map((el: { img: string }) => el.img);
    const signature = JSON.stringify(selectedCardImages);
    if (lastSavedCardsSignatureRef.current === signature) {
      return;
    }

    lastSavedCardsSignatureRef.current = signature;
    const data = {
      date_time: new Date().toISOString(),
      cards: selectedCardImages,
      comment: '',
    };

    setResult((prev) => {
      return { ...prev, mac_cards: [...prev.mac_cards, data] };
    });
  }, [selectedCards, setResult]);

  if (activeTool === PsyToolsId.MAC_CARDS) {
    if (selectedCards.length > 0) {
      if (mobile) {
        return <MacResultsMobile cards={selectedCards} />;
      }

      return (
        <div
          className={classNames('h-full w-full flex flex-row', mobile ? 'p-2' : 'pr-[0.25rem]', {
            'lg:min-w-[600px] md:min-w-[400px]': selectedCards.length <= 0,
          })}
        >
          <MacCardsSelected selectedCards={selectedCards} onCardZoom={onCardZoom} />
        </div>
      );
    }
    if (clientParticipantIds.length > 0) {
      const activeId =
        activeClientTab && clientParticipantIds.includes(activeClientTab) ? activeClientTab : clientParticipantIds[0];

      return (
        <div className="min-w-[300px] h-full flex flex-col bg-ink-muted rounded-card p-4 overflow-hidden">
          <Heading size="md" as="h3" tone="inverse" className="mb-1 pr-10">
            МАК-карты
          </Heading>
          <p className="text-white text-ui mb-3 pr-10">
            Клиенты сделали свой выбор. Выберите вкладку, чтобы посмотреть результат каждого.
          </p>
          <div className="flex flex-wrap gap-2 border-b border-[rgba(255,255,255,0.2)] pb-2 mb-3 shrink-0">
            {clientParticipantIds.map((participantId, idx) => {
              const isActive = participantId === activeId;

              return (
                <Pressable
                  key={participantId}
                  aria-label={`Участник ${idx + 1}`}
                  aria-pressed={isActive}
                  onClick={() => setActiveClientTab(participantId)}
                  className={classNames(
                    'flex items-center justify-center w-8 h-8 rounded-full text-ui-strong text-white transition-colors',
                    isActive ? 'bg-black' : 'bg-black/60 hover:bg-black/80',
                  )}
                >
                  {idx + 1}
                </Pressable>
              );
            })}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {activeId && clientSelectionsByParticipant[activeId]?.length > 0 ? (
              <MacCardsSelected selectedCards={clientSelectionsByParticipant[activeId]} onCardZoom={onCardZoom} />
            ) : (
              <p className="text-white/70 text-ui">Нет выбранных карт</p>
            )}
          </div>
        </div>
      );
    }
    if (mobile) {
      return <MacCardPrompt coverUp={coverDown} mobile />;
    }

    return (
      <div className="sm:w-[30vw] lg:w-[15vw]  min-w-[300px] h-full flex flex-col ">
        <MacCardPrompt coverUp={coverDown} />
      </div>
    );
  }

  if (mobile) {
    const coverModes = [
      { value: false, label: 'Рубашкой вверх' },
      { value: true, label: 'Картинкой вверх' },
    ];

    return (
      <div className="flex w-full flex-col gap-3">
        <p className="text-ui text-white/80">Выберите колоду и как клиент её увидит.</p>
        <div
          role="radiogroup"
          aria-label="Как показать колоду"
          className="grid grid-cols-2 rounded-full bg-black/25 p-1"
        >
          {coverModes.map((mode) => (
            <Pressable
              key={mode.label}
              role="radio"
              aria-checked={coverDown === mode.value}
              onClick={() => setCoverDown(mode.value)}
              className={classNames(
                'rounded-full px-3 py-2 text-ui font-medium transition-colors',
                coverDown === mode.value ? 'bg-accent text-ink' : 'text-white/80',
              )}
            >
              {mode.label}
            </Pressable>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-4">
          {arr &&
            arr.map((el, idx) => (
              <DeckPreview
                key={el?.id ?? idx}
                mobile
                deck={el}
                coverDown={coverDown}
                isSelected={selectedCard === el?.id}
                onSelect={(id) => setSelectedCard(id)}
                onInspect={(deck) => setInspectedDeck(deck)}
              />
            ))}
        </div>
        <div className="sticky bottom-[-1rem] z-40 -mx-4 bg-ink-muted px-4 pb-4 pt-3 shadow-overlay">
          <Button size="lg" fullWidth disabled={!selectedCard} onClick={() => activateTool()}>
            Показать колоду
          </Button>
        </div>
        <MacDeckGalleryModal
          isOpen={!!inspectedDeck}
          deckName={inspectedDeck?.name}
          cards={Array.isArray(inspectedDeck?.value) ? (inspectedDeck?.value as string[]) : []}
          onClose={() => setInspectedDeck(null)}
        />
      </div>
    );
  }

  return (
    <div className="w-full min-w-[300px] lg:w-[15vw] lg:min-w-[500px] h-full flex flex-col">
      <p className="ml-[1rem] mr-14 mt-[1rem] text-body-l text-white pb-[1rem]">
        Выберите колоду для показа клиенту и способ ее отображения: рубашкой вверх или вниз.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-[30px] lg:gap-[12px] max-h-[80vh] overflow-y-auto pr-4 pl-4 white-scroll">
        {arr &&
          arr.map((el, idx) => (
            <DeckPreview
              key={el?.id ?? idx}
              deck={el}
              coverDown={coverDown}
              isSelected={selectedCard === el?.id}
              onSelect={(id) => setSelectedCard(id)}
              onInspect={(deck) => setInspectedDeck(deck)}
            />
          ))}
      </div>

      <div className="mt-auto flex gap-[10px] pt-4">
        <Button
          size="md"
          variant="neutral"
          iconOnly
          aria-label="Карты рубашкой вверх"
          pressed={coverDown}
          onClick={() => setCoverDown(!coverDown)}
        >
          <ShuffleIcon />
        </Button>
        <Button
          size="md"
          fullWidth
          disabled={!selectedCard}
          onClick={() => {
            activateTool();
          }}
        >
          Показать колоду
        </Button>
      </div>
      <MacDeckGalleryModal
        isOpen={!!inspectedDeck}
        deckName={inspectedDeck?.name}
        cards={Array.isArray(inspectedDeck?.value) ? (inspectedDeck?.value as string[]) : []}
        onClose={() => setInspectedDeck(null)}
      />
    </div>
  );
};
